/**
 * Provider clinic-assignment normalization (#98).
 *
 * Post-#98 provider docs carry `clinicIds: string[]` (clinics the provider
 * works at) and `defaultForClinicIds: string[]` (clinics where they are the
 * background-check default). Pre-#98 docs carry a single `clinicId` plus an
 * `isDefault` flag. Every reader — the frontend Firestore converter, the
 * functions loadProviders, and firestore.rules (which necessarily keeps its
 * own copy in rules language) — must agree on how a legacy doc maps into
 * the array shape, so the mapping lives here once.
 *
 * Legacy semantics: a missing OR empty-string `clinicId` means the doc is
 * an orphan → both arrays come back empty. (Empty string is not a real
 * clinic id; treating it as one would fabricate an assignment.)
 */

export interface ProviderClinicFields {
  clinicIds: string[]
  defaultForClinicIds: string[]
}

export function normalizeProviderClinicFields(data: {
  clinicIds?: unknown
  defaultForClinicIds?: unknown
  clinicId?: unknown
  isDefault?: unknown
}): ProviderClinicFields {
  const legacyClinicId =
    typeof data.clinicId === 'string' && data.clinicId ? data.clinicId : null
  const clinicIds = Array.isArray(data.clinicIds)
    ? data.clinicIds as string[]
    : legacyClinicId ? [legacyClinicId] : []
  const defaultForClinicIds = Array.isArray(data.defaultForClinicIds)
    ? data.defaultForClinicIds as string[]
    : data.isDefault === true && legacyClinicId ? [legacyClinicId] : []
  // Defaults are only meaningful at clinics the provider is assigned to —
  // the write path enforces the subset (rules hasAll), and read-time
  // normalization applies the same constraint so corrupt or pathological
  // hybrid docs can't make a provider "default" somewhere they don't work.
  return {
    clinicIds,
    defaultForClinicIds: defaultForClinicIds.filter(c => clinicIds.includes(c)),
  }
}
